/**
 * Who, among families, must be able to SEE a class - the client half.
 *
 * The server half is netlify/functions/lib/classFamilyAudience.mjs, which owns
 * the same walk for refreshFamilyVisibilitySnapshots and the reservation
 * recipients backfill. Two audiences are stamped from the browser at the
 * moment a doc is written: a booking (lib/reservations.ts createReservation,
 * which is also how a REHEARSAL gets made) and a class message
 * (lib/classPosts.ts, lib/assistant.ts). A snapshot written roster-only here
 * would be wrong from birth and would sit wrong until someone re-ran the
 * backfill.
 *
 * Bobbie, 2026-08-27: "Parents must be able to see all of the classes their jr
 * staff dancer is teaching and assisting with." A Jr Staff dancer is a dancer
 * who is also staff; their family was only ever picked up when the dancer was
 * on the roster, never when the dancer was running the class.
 *
 * THE JR STAFF HALF IS ADMIN-BACKED, NOT A CLIENT FIRESTORE READ (Codex round
 * 7, PR #799, confirmed against the sources). This file used to read
 * users/{staffUid} directly. shared/firestore.rules' users read clause only
 * lets a NON-DIRECTOR staff caller read a target whose status is 'active' - a
 * live dancer-login helper's doc carries status 'dancer' (round 6, PR #799),
 * so for any non-Director sender that read was DENIED, silently swallowed by
 * this function's own never-throws contract, and the helper's family missed
 * the client-stamped booking or message. Widening the users read rules for
 * this was considered and rejected (it cuts against Bobbie's 2026-08-05
 * parent-contact exclusion and spends rules-cliff headroom), so this now
 * calls netlify/functions/jrStaffFamilyAudience.mjs instead - an admin-backed
 * endpoint that runs the SAME server walk every other lane already uses
 * (jrStaffFamilyEntriesForOffering via jrStaffFamilyUidsForOffering in
 * lib/classFamilyAudience.mjs) and gates the caller on active staff, never a
 * second spelling of the walk and never open to a family caller.
 *
 * There is deliberately no client-side copy of the staff-account walk
 * anymore - a dead twin invites drift, and this endpoint IS the one place
 * that decides who counts, on both server AND client callers now.
 */

import type { User } from 'firebase/auth';
import { auth } from './firebase';
import { functionHeaders } from './functionSecurity';
import { offeringStaffUids, type ClassStaffFields } from './offeringStaffUids';

/**
 * The staff-uid rule moved to its own file so the test can RUN it against the
 * server's copy instead of reading this one. It is re-exported here because it
 * is half of what this module is: see offeringStaffUids.ts for why it must not
 * borrow the teacher-authority helper, which is how the two halves came to
 * disagree about a class with no lead teacher.
 */
export { offeringStaffUids };
export type { ClassStaffFields };

/** Sorted, de-duplicated union of the roster lane and the Jr Staff lane. */
export function mergeReservationFamilyUids(
  enrolledFamilyUids: readonly string[],
  jrStaffFamilyUids: readonly string[]
): string[] {
  const clean = (list: readonly string[]) =>
    list.filter((uid): uid is string => typeof uid === 'string' && !!uid.trim());
  return [...new Set([...clean(enrolledFamilyUids), ...clean(jrStaffFamilyUids)])].sort();
}

const JR_STAFF_FAMILY_AUDIENCE_ENDPOINT = '/.netlify/functions/jrStaffFamilyAudience';

/**
 * IN-FLIGHT DEDUPE ONLY - NO RESULT CACHE (Codex round 10, PR #799,
 * correcting round 9's own premise). Round 9 added a 5-minute TTL result
 * cache alongside this, on the reasoning that "the server re-checks at
 * delivery time anyway". That reasoning was wrong for THIS caller: the
 * uids this function returns are stamped straight into
 * expectedRecipientUids on a NEW classPost or reservation
 * (src/lib/classPosts.ts, src/lib/assistant.ts, src/lib/reservations.ts),
 * and shared/firestore.rules intentionally grants family DOCUMENT READ
 * access from that exact stamp (isReservationRecipient and its class-post
 * twin) - the notification-time re-checks rounds 5-8 built never revoke a
 * document read already granted. A helper revoked inside a 5-minute TTL
 * window would still get her family stamped onto, and able to READ, every
 * new post and booking created from the cached answer - a real widening,
 * not a convenience one. So: every non-concurrent resolution must be
 * fresh. src/lib/videoPlayback.ts's resolveR2PlaybackUrl precedent this
 * result cache was modeled on does not actually apply here - a presigned
 * playback URL grants nothing beyond itself (a few hours of watching one
 * video), never a standing document-read grant, so caching it briefly was
 * never the same kind of decision.
 *
 * The IN-FLIGHT map alone stays (Codex round 9, PR #799, the half of that
 * round unaffected by this correction): concurrent callers for the SAME
 * offering (an import fires bursts, one createReservation per booking)
 * share ONE request rather than firing a second. This grants nothing
 * stale - every one of those concurrent callers gets the SAME single fresh
 * answer, computed from data read at essentially the same instant, which
 * is exactly what would happen if they raced two separate requests
 * instead. Only REPEAT (non-concurrent) resolution of the same offering
 * a while later must go back to the network, which is why there is no
 * companion result cache here the way there is one for playback: the
 * server round trip is the answer's only honest source now that round 9
 * raised the budget (800 per 10 minutes) to cover the full 500-booking
 * import shape even at one call per booking - the cache was belt-and-
 * suspenders capacity relief the access semantics cannot afford, not load
 * relief this endpoint still needs.
 */
const jrStaffFamilyAudienceInflight = new Map<string, Promise<string[]>>();

/** The real network call, allowed to throw - the public function below is what turns a rejection into the fail-closed []. */
async function fetchJrStaffFamilyUidsForOffering(
  offeringId: string,
  user: User,
  fetchImpl: typeof fetch
): Promise<string[]> {
  const headers = await functionHeaders(user, { json: false });
  const url = `${JR_STAFF_FAMILY_AUDIENCE_ENDPOINT}?offeringId=${encodeURIComponent(offeringId)}`;
  const response = await fetchImpl(url, { method: 'GET', headers });
  if (!response.ok) throw new Error(`jrStaffFamilyAudience responded ${response.status}`);
  const body: unknown = await response.json();
  const familyUids = body && typeof body === 'object'
    ? (body as { familyUids?: unknown }).familyUids
    : null;
  if (!Array.isArray(familyUids)) throw new Error('jrStaffFamilyAudience returned a malformed body');
  return familyUids.filter((uid): uid is string => typeof uid === 'string' && !!uid.trim()).sort();
}

/**
 * In-flight-then-fetch, no result cache (see the header comment above for
 * why a result cache is wrong here even though src/lib/videoPlayback.ts's
 * resolveR2PlaybackUrl keeps one for the analogous-looking problem). A
 * rejected fetch is never left sitting in the in-flight map either - the
 * `.finally()` below always clears it, success or failure, so the very
 * next call (even one already queued behind this same offeringId) gets a
 * real, fresh attempt.
 */
function resolveJrStaffFamilyUidsForOffering(
  offeringId: string,
  user: User,
  fetchImpl: typeof fetch
): Promise<string[]> {
  const pending = jrStaffFamilyAudienceInflight.get(offeringId);
  if (pending) return pending;
  const promise = fetchJrStaffFamilyUidsForOffering(offeringId, user, fetchImpl)
    .finally(() => {
      jrStaffFamilyAudienceInflight.delete(offeringId);
    });
  jrStaffFamilyAudienceInflight.set(offeringId, promise);
  return promise;
}

/**
 * The families of any Jr Staff dancer who teaches, co-teaches, or assists this
 * class. Empty when the class has no staff, when its staff are adults with no
 * dancer behind the account, or when those dancers have no family linked -
 * and empty (never thrown) when the caller is not signed in, the endpoint is
 * unreachable, or it answers with anything but 200. A failed or missing
 * response reproduces only the ORIGINAL bug (a family not yet added), never a
 * crash of the write that is asking.
 *
 * @param {string | null | undefined} offeringId
 * @param {{ fetchImpl?: typeof fetch }} [deps] test seam only; production
 *   callers never pass this.
 */
export async function jrStaffFamilyUidsForOffering(
  offeringId: string | null | undefined,
  deps: { fetchImpl?: typeof fetch } = {}
): Promise<string[]> {
  const user = auth?.currentUser;
  if (!offeringId || !user) return [];
  try {
    return await resolveJrStaffFamilyUidsForOffering(offeringId, user, deps.fetchImpl ?? fetch);
  } catch {
    return [];
  }
}
